import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Flex, Box, SlideFade } from '@wegroup/design-system';

import { useLocation } from 'react-router';
import ScrollDownButton from '../../../../flows/src/components/ui/advisory-flow/components/scroll-button/ScrollDownButton';
import {
  bp,
  bph,
  customMediaHWQuery,
  customScrollBar,
  mq,
} from '../../../../common/src/utils/styledComponentsUtils';
import { useFormContext } from 'react-hook-form';

interface Props {
  isHomeQuestion?: boolean;
  resetScrollOnChange?: unknown;
  maxW?: string;
}

const ScrollableContentContainer: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  isHomeQuestion,
  resetScrollOnChange,
  maxW = '100rem',
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const scrollDiv = ref.current;
  const location = useLocation();
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const formContext = useFormContext();

  useEffect(() => {
    scrollDiv?.scrollTo(0, 0);
  }, [resetScrollOnChange || location.pathname]);

  useEffect(() => {
    if (ref.current?.scrollTop === 0) {
      setIsScrollable(
        (ref.current?.scrollHeight || 0) > (ref.current?.clientHeight || 0),
      );
    }
  }, [
    ref.current?.clientHeight,
    ref.current?.scrollHeight,
    resetScrollOnChange,
    location.pathname,
  ]);

  const handleScroll = () => {
    /**
     * This function will check if the scrollcontainer is scrolled all the way to the bottom
     */
    const offsetBottom = 50;
    if (
      scrollDiv &&
      scrollDiv.offsetHeight + scrollDiv.scrollTop >=
        scrollDiv.scrollHeight - offsetBottom
    ) {
      setIsScrollable(false);
    } else {
      setIsScrollable(true);
    }
  };

  const handleClickScroll = () => {
    if (scrollDiv) {
      scrollDiv.scrollTo({
        top: scrollDiv.scrollTop + scrollDiv.offsetHeight * 0.8,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Flex
      h="100%"
      flexDirection="column"
      position="relative"
      bg="white"
      onScroll={handleScroll}
    >
      {/* Shadow */}
      <Box
        display={{ base: 'block', laptop: 'none' }}
        bg="rgba(0,0,0, 0)"
        pointerEvents="none"
        h="20px"
        w="calc(100% - 12px)"
        position="absolute"
        bottom="0"
        boxShadow={
          isScrollable
            ? 'inset 0px -12px 8px -10px rgba(0, 0, 0, 0.04)'
            : 'unset'
        }
        zIndex="1"
        transition="0.2s"
      />
      {/* ---- */}
      <Container
        ref={ref}
        data-test-id="ProdFac_ScrollableContentContainer"
        isHomeQuestion={isHomeQuestion}
        onScroll={handleScroll}
        hasShadow={isScrollable}
        {...rest}
      >
        <Flex
          flexDirection="column"
          maxW={maxW}
          alignSelf="center"
          w="100%"
          flexShrink={0}
          sx={{
            '> *': { flexShrink: 1 },
          }}
          data-test-id="ProdFac_QuestionGroupWrapper_Grid"
        >
          {children}
        </Flex>
      </Container>
      <Box
        position="absolute"
        w="44px"
        h="44px"
        bottom="3"
        right="4"
        zIndex="2"
      >
        <SlideFade
          in={isScrollable && !formContext?.formState.isSubmitting}
          offsetY="20px"
          unmountOnExit
        >
          <ScrollDownButton onClick={handleClickScroll} />
        </SlideFade>
      </Box>
    </Flex>
  );
};

const Container = styled.div<{ isHomeQuestion?: boolean; hasShadow?: boolean }>`
  background: white;
  width: 100%;
  max-width: 100vw;
  overflow-y: auto;
  height: 100%;
  padding: 1.6rem 2.4rem 4rem;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;

  &:nth-child(3) {
    margin-bottom: 0;
  }

  ${mq.laptop} {
    background: ${({ isHomeQuestion }) =>
      isHomeQuestion ? '#fff' : '#fdfcff'};
    border-top-left-radius: ${({ isHomeQuestion }) =>
      isHomeQuestion ? '20px' : '0'};
    border-top-right-radius: ${({ isHomeQuestion }) =>
      isHomeQuestion ? '20px' : '0'};
    &:nth-child(3) {
      margin-bottom: unset;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }

  ${customMediaHWQuery(bp.laptop, bph.hd)} {
    padding: 4rem 4rem 6rem;
  }

  ${customScrollBar}

  &::-webkit-scrollbar-thumb, &::-webkit-scrollbar-track-piece {
    border-color: ${({ isHomeQuestion }) =>
      isHomeQuestion ? '#fff' : '#fdfcff'};
  }
`;

export default ScrollableContentContainer;
